import styles from "./Header.module.css";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import logo from "../../assets/media/logo.png";
import SubscribeCTA from "../SubscribeCTA/SubscribeCTA";
import SocialConnectMenu from "../SocialConnectMenu/SocialConnectMenu";
import { lockBodyScroll, unlockBodyScroll } from "../../Hooks/bodyScrollLock";
import { pickContentByType } from "../../data/contentPicker";

/**
 * Header holds the *small* bar logo only.
 *
 * Large “hero” logo lives in the page welcome section (see Home) so it scrolls
 * with the decoration triangle as normal document content. When scroll passes
 * the hero handoff point, this compact logo is shown.
 *
 * Visibility is driven every animation frame from window.scrollY (not
 * IntersectionObserver / scroll events alone), so it tracks mid-gesture.
 */
function Header(props) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const logoWrapRef = useRef(null);
  const menuOpenButtonRef = useRef(null);
  const menuSheetRef = useRef(null);
  /** True when this menu session forced the bar logo in (so we reverse on close). */
  const menuForcedLogoRef = useRef(false);
  const { content } = useSelector((state) => state.contentData);

  const menuOrderItem = pickContentByType(content, "menuOrder");
  const menuOrderCsv =
    menuOrderItem && menuOrderItem.menuOrder != null
      ? String(menuOrderItem.menuOrder).replaceAll(" ", "")
      : "";
  const menuOrder = menuOrderCsv
    ? menuOrderCsv.split(",").filter(Boolean)
    : null;

  const navLinks = [];
  if (content) {
    if (props.musicIsActive)
      navLinks.push({
        _id: "music",
        type: "music",
        titleOnNavMenu: "Music",
        title: "Music2",
      });

    for (const value of Object.values(content)) {
      if (
        value.hasOwnProperty("active") &&
        value.active !== false &&
        value.active !== "false" &&
        value.active.replace(" ", "") !== ""
      ) {
        if (
          value.hasOwnProperty("addToNavMenu") &&
          value.addToNavMenu !== false &&
          value.addToNavMenu !== "false" &&
          value.addToNavMenu.replace(" ", "") !== ""
        )
          navLinks.push(value);
      }
    }
  }
  const sortedNavLinks = menuOrder ? [] : [...navLinks];
  if (menuOrder)
    menuOrder.forEach((menuItemName) => {
      const matchedNavLink = navLinks.filter((navLink) => {
        return navLink.type === menuItemName;
      });

      if (matchedNavLink[0]) sortedNavLinks.push(matchedNavLink[0]);
    });

  const hasMusicEntry =
    !!props.musicIsActive ||
    sortedNavLinks.some((link) => link.type === "music");
  /* When Listen CTA is shown, keep Explore free of a duplicate Music row */
  const exploreNavLinks = hasMusicEntry
    ? sortedNavLinks.filter((link) => link.type !== "music")
    : sortedNavLinks;

  const setMobileMenu = useCallback((open) => {
    setMobileMenuOpen(open);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((open) => !open);
  }, []);

  // Escape, focus trap, body scroll lock, restore focus when menu closes
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previouslyFocused = document.activeElement;
    lockBodyScroll();

    // Keep focus on the bar toggle (primary open/close control)
    const focusId = window.requestAnimationFrame(() => {
      menuOpenButtonRef.current?.focus();
    });

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setMobileMenuOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const sheetFocusable = menuSheetRef.current
        ? Array.from(
            menuSheetRef.current.querySelectorAll(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter(
            (el) => el.offsetParent !== null || el === document.activeElement,
          )
        : [];
      const bar = menuOpenButtonRef.current;
      const list = bar ? [bar, ...sheetFocusable] : sheetFocusable;
      if (list.length === 0) return;

      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusId);
      window.removeEventListener("keydown", onKeyDown);
      unlockBodyScroll();
      const restoreTarget =
        menuOpenButtonRef.current ||
        (previouslyFocused && previouslyFocused.focus
          ? previouslyFocused
          : null);
      if (restoreTarget && typeof restoreTarget.focus === "function") {
        restoreTarget.focus();
      }
    };
  }, [mobileMenuOpen]);

  /*
    Menu-driven bar logo:
    If the compact logo is not already slid in (hero still owns the brand),
    force it in while the menu is open, then reverse that force on close.
    Scroll-driven logo (already visible) is left alone.
  */
  useEffect(() => {
    const root = document.documentElement;
    const logoEl = logoWrapRef.current;
    const FORCE_MS = 340;

    if (mobileMenuOpen) {
      const hasHero = root.getAttribute("data-has-hero-logo") === "true";
      let alreadyVisible = !hasHero;

      if (hasHero && logoEl) {
        const opacity = parseFloat(getComputedStyle(logoEl).opacity);
        alreadyVisible = !Number.isNaN(opacity) && opacity > 0.85;
      }

      if (alreadyVisible) {
        menuForcedLogoRef.current = false;
        root.removeAttribute("data-menu-logo-force");
        return undefined;
      }

      menuForcedLogoRef.current = true;
      // Lock at hidden without transition, then flip to "in" so slide animates.
      root.setAttribute("data-menu-logo-force", "prep");
      logoEl?.setAttribute("aria-hidden", "false");

      let raf2 = 0;
      const raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          root.setAttribute("data-menu-logo-force", "in");
        });
      });

      return () => {
        window.cancelAnimationFrame(raf1);
        if (raf2) window.cancelAnimationFrame(raf2);
      };
    }

    // Closing: only reverse if this session forced the logo in
    if (!menuForcedLogoRef.current) {
      root.removeAttribute("data-menu-logo-force");
      return undefined;
    }

    root.setAttribute("data-menu-logo-force", "out");
    const timer = window.setTimeout(() => {
      root.removeAttribute("data-menu-logo-force");
      menuForcedLogoRef.current = false;
      // Let scroll/JS handoff re-assert aria if still at top of page
      if (logoEl && root.getAttribute("data-has-hero-logo") === "true") {
        const y = window.scrollY || window.pageYOffset || 0;
        if (y < 130) logoEl.setAttribute("aria-hidden", "true");
      }
    }, FORCE_MS);

    return () => window.clearTimeout(timer);
  }, [mobileMenuOpen]);

  // Logo handoff:
  // - Prefer CSS animation-timeline: scroll(root) on both logos (compositor;
  //   updates WHILE scrolling). See Header.module.css + Home.module.css.
  // - html[data-has-hero-logo] enables that path only on pages with #hero-logo.
  // - JS CSS-variable fallback only when scroll timelines are unavailable.
  useEffect(() => {
    const logoEl = logoWrapRef.current;
    if (!logoEl) return;

    const root = document.documentElement;
    const supportsScrollTimeline =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      (CSS.supports("animation-timeline", "scroll()") ||
        CSS.supports("animation-timeline", "scroll(root)"));

    const navbarPx =
      parseFloat(
        getComputedStyle(root).getPropertyValue("--ms1-navbar-height"),
      ) || 50;

    let hasHero = false;
    let handoffY = 0;
    let showHeader = null;
    let running = true;
    let rafId = 0;
    let frames = 0;

    const setHasHeroAttr = (on) => {
      if (on) root.setAttribute("data-has-hero-logo", "true");
      else root.removeAttribute("data-has-hero-logo");
    };

    const setHeaderMode = (nextShowHeader) => {
      if (nextShowHeader === showHeader) return;
      showHeader = nextShowHeader;

      root.style.setProperty("--ms1-logo-header", nextShowHeader ? "1" : "0");
      root.style.setProperty("--ms1-logo-hero", nextShowHeader ? "0" : "1");

      logoEl.style.pointerEvents = nextShowHeader ? "auto" : "none";
      logoEl.setAttribute("aria-hidden", nextShowHeader ? "false" : "true");

      const hero = document.getElementById("hero-logo");
      if (hero) {
        hero.style.pointerEvents = nextShowHeader ? "none" : "auto";
        hero.setAttribute("aria-hidden", nextShowHeader ? "true" : "false");
      }
    };

    const measureHero = () => {
      const hero = document.getElementById("hero-logo");
      if (!hero) {
        hasHero = false;
        handoffY = 0;
        setHasHeroAttr(false);
        return;
      }
      hasHero = true;
      setHasHeroAttr(true);
      const rect = hero.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const heroDocTop = rect.top + scrollY;
      handoffY = Math.max(0, heroDocTop + rect.height * 0.4 - navbarPx);
    };

    measureHero();

    // CSS scroll timelines drive motion when supported + hero present.
    if (supportsScrollTimeline) {
      // Keep data-has-hero-logo in sync as routes mount/unmount.
      const pollId = window.setInterval(measureHero, 400);
      const stopPoll = window.setTimeout(
        () => window.clearInterval(pollId),
        5000,
      );

      // Default vars for non-hero pages (Manage etc.)
      if (!hasHero) setHeaderMode(true);
      else {
        // Leave opacity/transform to CSS animation; still set a11y roughly.
        setHeaderMode(false);
      }

      return () => {
        window.clearInterval(pollId);
        window.clearTimeout(stopPoll);
        setHasHeroAttr(false);
        root.style.removeProperty("--ms1-logo-header");
        root.style.removeProperty("--ms1-logo-hero");
      };
    }

    // --- Fallback: no scroll timeline (older browsers) ---
    const tick = () => {
      if (!running) return;
      frames += 1;
      if (frames % 30 === 1) measureHero();

      if (!hasHero) setHeaderMode(true);
      else {
        const y = window.scrollY || window.pageYOffset || 0;
        setHeaderMode(y >= handoffY);
      }
      rafId = window.requestAnimationFrame(tick);
    };

    if (hasHero) {
      const y = window.scrollY || window.pageYOffset || 0;
      setHeaderMode(y >= handoffY);
    } else {
      setHeaderMode(true);
    }

    rafId = window.requestAnimationFrame(tick);
    window.addEventListener("resize", measureHero, { passive: true });

    return () => {
      running = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measureHero);
      setHasHeroAttr(false);
      root.style.removeProperty("--ms1-logo-header");
      root.style.removeProperty("--ms1-logo-hero");
    };
  }, []);

  return (
    <div id="ms1-header" className={styles.outerwrap}>
      {/*
        Mobile: full-bar control. Toggles open/close. While open, stays above the
        sheet (see CSS) so another tap on the bar closes without hunting for ✕.
      */}
      <button
        ref={menuOpenButtonRef}
        type="button"
        className={
          styles["mobile-menu-button"] +
          (mobileMenuOpen ? " " + styles["mobile-menu-button-open"] : "")
        }
        onClick={toggleMobileMenu}
        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={mobileMenuOpen}
        aria-controls="ms1-mobile-menu"
      >
        <span className={styles["mobile-menu-lines"]} aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
        <span className={styles["mobile-menu-hamburger"]} aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>
      <div ref={logoWrapRef} className={styles["logo-title-outerwrap"]}>
        <a href="/" className={styles["header-logo-link"]}>
          <img
            src={logo}
            alt="Ignite Elysium"
            className={styles["header-logo-img"]}
          />
        </a>
      </div>
      <div className={styles["header-functions-container"]}>
        <div className={styles["subscribe-cta-wrap"]}>
          <SubscribeCTA layout="compact" revealOnClick />
        </div>
        <div className={styles["nav-container"]}>
          {navLinks &&
            sortedNavLinks.map((item) => (
              <a
                key={item._id + item.type}
                href={`#${item.type}`}
                alt=""
                className={styles["small-header-nav"]}
              >
                {item.titleOnNavMenu ? item.titleOnNavMenu : item.title}
              </a>
            ))}
          <SocialConnectMenu />
        </div>
      </div>

      {/* Full-bleed mobile menu sheet; backdrop tap, Escape, and ✕ all close */}
      <div
        id="ms1-mobile-menu"
        className={
          styles["menu-modal"] +
          (mobileMenuOpen ? " " + styles["menu-modal-open"] : "")
        }
        aria-hidden={!mobileMenuOpen}
        onClick={() => setMobileMenu(false)}
      >
        <div
          ref={menuSheetRef}
          className={styles["menu-sheet"]}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          data-scroll-lock-scrollable
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles["menu-sheet-glow"]} aria-hidden="true" />

          {/*
            Explicit close control (bar re-tap / backdrop / Escape still work).
            ROLLBACK: remove this button + .menu-close-* rules in Header.module.css.
          */}
          <button
            type="button"
            className={styles["menu-close-button"]}
            onClick={() => setMobileMenu(false)}
            aria-label="Close menu"
          >
            <span className={styles["menu-close-label"]}>Close</span>
            {/* <span className={styles["menu-close-icon"]} aria-hidden="true">
              ×
            </span> */}
          </button>

          <div className={styles["menu-sheet-body"]}>
            {hasMusicEntry && (
              <a
                href="#music"
                className={styles["menu-listen-cta"]}
                onClick={() => setMobileMenu(false)}
              >
                Listen now
              </a>
            )}

            {exploreNavLinks.length > 0 && (
              <section className={styles["menu-section"]}>
                <p className={styles["menu-section-label"]}>Explore</p>
                <nav className={styles["menu-nav"]} aria-label="Primary">
                  {exploreNavLinks.map((item, index) => (
                    <a
                      key={item._id + item.type}
                      href={`#${item.type}`}
                      className={styles["menu-nav-link"]}
                      style={{ "--menu-stagger": String(index) }}
                      onClick={() => setMobileMenu(false)}
                    >
                      {item.titleOnNavMenu ? item.titleOnNavMenu : item.title}
                    </a>
                  ))}
                </nav>
              </section>
            )}

            <section className={styles["menu-section"]}>
              <p className={styles["menu-section-label"]}>
                Listen &amp; follow
              </p>
              <p className={styles["menu-section-lede"]}>
                Streams, socials, and the latest drops
              </p>
              <div className={styles["menu-social-expanded"]}>
                <SocialConnectMenu
                  disableModal={true}
                  socialContainerStyles={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                  linksContainerStyles={{
                    flexDirection: "column",
                    flexWrap: "nowrap",
                    justifyContent: "flex-start",
                    alignItems: "stretch",
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "visible",
                    height: "auto",
                    gap: 0,
                  }}
                  linksStyles={{ margin: 0 }}
                />
              </div>
            </section>

            <section
              className={
                styles["menu-section"] + " " + styles["menu-section-subscribe"]
              }
            >
              <p className={styles["menu-section-label"]}>Stay in touch</p>
              <p className={styles["menu-section-lede"]}>
                Releases, shows, and updates with no spam ever
              </p>
              <div className={styles["menu-subscribe-wrap"]}>
                <SubscribeCTA submitLabel="Join" />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
